import { useState } from 'react';
import { useEditorStore } from '../../store/editorStore';
import { TEXT_TEMPLATES, TEMPLATE_CATEGORIES } from '../../data/textTemplates';

export default function TextTemplatesPanel() {
  const [activeCategory, setActiveCategory] = useState('All');
  const [hoveredId, setHoveredId] = useState(null);

  const activePageId = useEditorStore((s) => s.activePageId);
  const story = useEditorStore((s) => s.story);
  const addLayer = useEditorStore((s) => s.addLayer);

  const filtered = activeCategory === 'All'
    ? TEXT_TEMPLATES
    : TEXT_TEMPLATES.filter((t) => t.category === activeCategory);

  function handleApply(template) {
    if (!activePageId || !story) return;

    const basePage = story.pages.find((p) => p.id === activePageId);
    const baseZIndex = basePage ? basePage.layers.length : 0;

    template.layers.forEach((layerDef, i) => {
      addLayer(activePageId, layerDef.data, {
        x: layerDef.transform.x,
        y: layerDef.transform.y,
        width: layerDef.transform.width,
        height: layerDef.transform.height,
        rotation: layerDef.transform.rotation ?? 0,
        opacity: layerDef.transform.opacity ?? 1,
        zIndex: baseZIndex + (layerDef.transform.zIndex ?? i + 1),
      });
    });
  }

  return (
    <div>
      {/* Category Filters */}
      <div className="overflow-x-auto pb-1 mb-2">
        <div className="flex gap-1" style={{ minWidth: 'max-content' }}>
          {TEMPLATE_CATEGORIES.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className="px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-colors"
              style={{
                background: activeCategory === cat ? 'var(--brand-primary)' : 'var(--t-panel)',
                color: activeCategory === cat ? '#fff' : 'var(--t-muted)',
                border: `1px solid ${activeCategory === cat ? 'var(--brand-primary)' : 'var(--t-border)'}`,
                cursor: 'pointer',
              }}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* Template Grid */}
      <div>
        <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
          {filtered.map((template) => (
            <button
              key={template.id}
              onClick={() => handleApply(template)}
              onMouseEnter={() => setHoveredId(template.id)}
              onMouseLeave={() => setHoveredId(null)}
              className="relative rounded-lg overflow-hidden text-left transition-all"
              style={{
                border: `1.5px solid ${hoveredId === template.id ? 'var(--brand-primary)' : 'var(--t-border)'}`,
                background: 'var(--t-panel)',
                cursor: 'pointer',
                transform: hoveredId === template.id ? 'translateY(-1px)' : 'none',
                boxShadow: hoveredId === template.id ? '0 4px 16px rgba(99,102,241,0.2)' : 'none',
                transition: 'all 0.15s ease',
                aspectRatio: '9/16',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 140,
              }}
              title={`Apply: ${template.name}`}
            >
              {/* Preview */}
              <div
                style={{
                  flex: 1,
                  overflow: 'hidden',
                  position: 'relative',
                  ...template.previewStyle,
                }}
              >
                {template.previewContent.map((item, i) => (
                  <div key={i} style={{ ...item.style, maxWidth: '100%', textAlign: item.style.textAlign ?? 'center' }}>
                    {item.text}
                  </div>
                ))}
              </div>

              {/* Label */}
              <div
                className="shrink-0 px-2 py-1.5 flex items-center justify-between"
                style={{ background: 'var(--t-surface)', borderTop: '1px solid var(--t-border)' }}
              >
                <span className="text-xs font-medium truncate" style={{ color: 'var(--t-text)', maxWidth: '70%' }}>
                  {template.name}
                </span>
                <span
                  className="text-[9px] rounded px-1 py-0.5 font-medium"
                  style={{ background: 'var(--t-panel)', color: 'var(--t-muted)' }}
                >
                  {template.category}
                </span>
              </div>

              {/* Hover overlay */}
              {hoveredId === template.id && (
                <div
                  className="absolute inset-0 flex items-center justify-center"
                  style={{ background: 'rgba(99,102,241,0.08)', pointerEvents: 'none' }}
                >
                  <div
                    className="text-xs font-semibold px-3 py-1.5 rounded-full"
                    style={{ background: 'var(--brand-primary)', color: '#fff', boxShadow: '0 2px 8px rgba(99,102,241,0.4)' }}
                  >
                    + Apply
                  </div>
                </div>
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
